import { useCallback, useRef, useState } from 'react';
import { API_V1 } from '../Constant';

/**
 * Looks up a GSTIN's registered business details (name, address, tax treatment)
 * via the public POST /api/v1/gstin/search endpoint. No credentials are sent —
 * the endpoint is public so it also works pre-auth (e.g. during registration).
 *
 * GSTIN is always optional for the caller: a failed/not-found lookup resolves to
 * null rather than throwing, so consumers should treat it as "couldn't autofill,
 * let the user fill it in manually" rather than a hard error.
 */
export function useGstinLookup() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const lastLookedUpRef = useRef(null);

    const lookupGstin = useCallback(async (gstin) => {
        if (!gstin) return null;
        // Avoid firing a second paid lookup for a GSTIN we already resolved
        // (e.g. re-blurring the same field without changing the value).
        if (lastLookedUpRef.current === gstin) return null;

        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_V1}/gstin/search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ gstin }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(body.message || 'Could not fetch GSTIN details.');
                return null;
            }
            lastLookedUpRef.current = gstin;
            return body.data;
        } catch {
            setError('Network error — could not fetch GSTIN details.');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return { lookupGstin, loading, error };
}
